(ns dre.components.searchbar.events
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.components.searchbar.util :as util]
   [dre.network :as-alias network]
   [dre.page-util :as page-util]
   [dre.shared.interface.search.pills :as pill-util]
   [dre.utils :as core-util]
   [dre.utils.query :as query-util]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(defn generate-search-input-id
  [db-root-path]
  (str "search-" (str/join "-" (map name  db-root-path))))

(defn- update-caret-position
  [db db-root-path op pill-count]
  (if (= -1 (get-in db (conj db-root-path :caret-position)))
    (assoc-in db (conj db-root-path :caret-position) ((case op
                                                        :inc inc
                                                        :dec dec
                                                        #_default identity) pill-count))
    (update-in db (conj db-root-path :caret-position) (case op
                                                        :inc inc
                                                        :dec dec
                                                        #_default identity))))

(defn- pop-from-future
  [db db-root-path]
  (update-in db (concat db-root-path [:time-travel :future])
             util/pop-or-identity))

(defn- pop-from-past
  [db db-root-path]
  (update-in db (concat db-root-path [:time-travel :past])
             util/pop-or-identity))

(defn- add-to-future
  [db db-root-path search-query]
  (update-in db (concat db-root-path [:time-travel :future])
             #(if (nil? %)
                '(search-query)
                (vec (take-last 10 (conj % search-query))))))

(defn- add-to-past
  [db db-root-path search-query]
  (update-in db (concat db-root-path [:time-travel :past])
             #(if (nil? %)
                [search-query]
                (vec (take-last 10 (conj % search-query))))))

(defn- clear-future
  [db db-root-path]
  (assoc-in db (concat db-root-path [:time-travel :future]) '()))

(defn- update-pills
  [db db-root-path {:keys [update-fn search-query on-change update-caret-op pill-count] :as opts}]
  {:pre [(util/has-keys? opts #{:update-fn :search-query})]}
  (let [old-search-query search-query
        old-pills (:query/pills search-query)
        search-query (->> old-pills
                          pill-util/pills->flats
                          pill-util/main-pills
                          update-fn
                          (pill-util/assoc-main-pills old-pills)
                          (assoc search-query :query/pills))
        on-change-res (when on-change (on-change search-query))
        update-id (random-uuid)]
    {:db (cond-> (update-in db db-root-path
                            #(assoc % :search-query search-query))
           update-caret-op (update-caret-position db-root-path update-caret-op pill-count)
           true (update-in db-root-path
                           #(assoc-in % [:update-id] update-id))
           true (add-to-past db-root-path old-search-query)
           true (clear-future db-root-path))
     :fx [[:dispatch-later {:ms 500
                            :dispatch [::fetch-count db-root-path {:search-query search-query :update-id update-id}]}] 
          (when on-change-res [:dispatch on-change-res])]}))

;; events

(rf/reg-event-fx
 ::fetch-suggestions
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [input limit] :as opts}]]
  {:pre [(vector? db-root-path)
         (util/has-keys? opts #{:input :caret-position :search-query})]}
  (if-let [suggestions (get-in db (conj db-root-path :suggestions input))]
    {:db (update-in db db-root-path
                    #(assoc-in % [:suggestions :current] suggestions))}
    {::network/request
     [{:endpoint (util/->suggestions-endpoint input)}
      {:params {:query (util/->suggestions-query input)
                :limit (or limit 10)}
       :envelope? false
       :on-success [::fetch-suggestions-on-success db-root-path opts]}]})))

(rf/reg-event-db
 ::fetch-suggestions-on-success
 [rf/trim-v]
 (fn-traced
  [db [db-root-path {:keys [input] :as opts} {:keys [body]}]]
  {:pre [(vector? db-root-path)
         (util/has-keys? opts #{:input :caret-position :search-query})]}
  (let [suggestions (-> (assoc opts :data body)
                        util/->suggestions)]
    (update-in db db-root-path
               #(-> %
                    (assoc-in [:suggestions input] suggestions)
                    (assoc-in [:suggestions :current] suggestions))))))

(rf/reg-event-fx
 ::insert-pill
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [pill on-change] :as opts}]]
  {:pre [(util/has-keys? opts #{:pill})]}
  (let [{:keys [caret-position search-query]} (get-in db db-root-path)
        pill-count (-> search-query :query/pills page-util/count-pills)
        caret-position (if (= -1 caret-position)
                         pill-count 
                         caret-position)]
    (when (and
           (some? pill)
           (>= caret-position 0)
           (<= caret-position pill-count))
      (let [pill (if (:pill/type pill)
                   (pill-util/pills->flats [pill])
                   [pill])
            update-fn #(page-util/insert-at % caret-position pill)]
        (update-pills db db-root-path {:update-fn update-fn
                                       :search-query search-query
                                       :on-change on-change
                                       :update-caret-op :inc
                                       :pill-count pill-count}))))))

(rf/reg-event-fx
 ::remove-pill
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [idx on-change update-caret-op] :as opts}]]
  {:pre [(util/has-keys? opts #{:idx})]}
  (let [search-query (get-in db (conj db-root-path :search-query))
        pill-count (-> search-query :query/pills page-util/count-pills)]
    (when (and (>= idx 0)
               (< idx pill-count))
      (update-pills db db-root-path {:update-fn #(page-util/remove-at % idx)
                                     :search-query search-query
                                     :on-change on-change
                                     :update-caret-op update-caret-op
                                     :pill-count pill-count})))))


(rf/reg-event-fx
 ::fetch-count
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [search-query update-id] :as opts}]]
  {:pre [(util/has-keys? opts #{:search-query})]}
  (when (or (not update-id)
            (= update-id (get-in db (conj db-root-path :update-id))))
   (if-let [cached-response (get-in db (conj db-root-path :counts search-query))]
    {:db (update-in db db-root-path
                    #(assoc-in % [:counts :current] cached-response))}
    (if-let [request-body (-> search-query
                              query-util/pills-api-params
                              core-util/stringify-keys-with-namespaces)]
      {:fx [[::network/request [{:endpoint :search/articles-old-count}
                                {:response-content-type :json
                                 :body request-body
                                 :envelope? false
                                 :on-success [::fetch-count-on-success db-root-path opts]}]]
            [:dispatch [::clear-counts db-root-path]]
            [:dispatch [::set-counts-loading db-root-path {:loading? true}]]]}
      {:dispatch [::clear-counts db-root-path]})))))

(rf/reg-event-db
 ::fetch-count-on-success
 [rf/trim-v]
 (fn-traced
  [db [db-root-path {:keys [search-query] :as opts} {:keys [body]}]]
  {:pre [(util/has-keys? opts #{:search-query})]}
  (update-in db db-root-path
             #(-> %
                  (assoc-in [:counts :loading?] false)
                  (assoc-in [:counts search-query] body)
                  (assoc-in [:counts :current] body)))))

(rf/reg-event-db
 ::clear-counts
 [rf/trim-v]
 (fn-traced
  [db [db-root-path]]
  (update-in db db-root-path
             #(-> %
                  (assoc-in [:counts :loading?] false)
                  (assoc-in [:counts :current] nil)))))

(rf/reg-event-db
 ::set-counts-loading
 [rf/trim-v]
 (fn-traced
  [db [db-root-path {:keys [loading?] :as opts}]]
  {:pre [(util/has-keys? opts #{:loading?})]}
  (update-in db db-root-path
             #(assoc-in % [:counts :loading?] loading?))))

(rf/reg-event-fx
 ::clear-query
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [on-change] :as opts}]]
  (let [empty-search-query {:query/pills []
                            :query/type :articles}
        on-change-res ((or on-change (constantly nil)) empty-search-query)]
    (cond-> {:db (-> (update-in db db-root-path
                                #(assoc % :search-query empty-search-query))
                     (clear-future db-root-path)
                     (add-to-past db-root-path (get-in db (conj db-root-path :search-query))))}
      on-change-res (update :fx conj [:dispatch on-change-res])))))

(rf/reg-event-db
 ::clear-suggestions
 [rf/trim-v]
 (fn-traced
  [db [db-root-path]]
  (update-in db db-root-path
             #(assoc-in % [:suggestions :current] nil))))

(rf/reg-event-db
 ::set-search-query
 [rf/trim-v]
 (fn-traced
  [db [db-root-path search-query]]
  (assoc-in db (conj db-root-path :search-query) search-query)))

(rf/reg-event-db
 ::update-caret-position
 [rf/trim-v]
 (fn-traced
  [db [db-root-path op]]
  (let [pill-count (-> db (get-in db-root-path) :search-query :query/pills page-util/count-pills)]
    (update-caret-position db db-root-path op pill-count))))

(rf/reg-event-db
 ::set-caret-position
 [rf/trim-v]
 (fn-traced
  [db [db-root-path i]]
  (assoc-in db (conj db-root-path :caret-position) i)))

(rf/reg-event-db
 ::set-caret-position-end
 [rf/trim-v]
 (fn-traced
  [db [db-root-path]]
  (let [pill-count (-> db (get-in db-root-path) :search-query :query/pills page-util/count-pills)]
    (assoc-in db (conj db-root-path :caret-position) pill-count))))

; --- TIME TRAVEL --- ;

(rf/reg-event-fx
 ::undo
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path]]
  (let [original-db db
        db (get-in db db-root-path)
        current-search-query (:search-query db)
        past-search-query (-> db
                              (get-in [:time-travel :past])
                              peek)]
    {:db (-> original-db 
             (pop-from-past db-root-path)
             (add-to-future db-root-path current-search-query))
     :fx [[:dispatch [::set-search-query db-root-path past-search-query]]]})))

(rf/reg-event-fx
 ::redo
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path]]
  (let [original-db db
        db (get-in db db-root-path)
        current-search-query (:search-query db)
        future-search-query (-> db
                                (get-in [:time-travel :future])
                                peek)]
    {:db (-> original-db
             (pop-from-future db-root-path)
             (add-to-past db-root-path current-search-query))
     :fx [[:dispatch [::set-search-query db-root-path future-search-query]]]})))

; --- TIME TRAVEL --- ;
